import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { useId } from 'react'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'

const controlClass =
  'w-full rounded-control border border-transparent bg-raise px-4 text-body-sm text-app-fg placeholder:text-faint transition-colors focus:border-outline focus:outline-none disabled:opacity-50'

interface FieldShellProps {
  label: string
  required?: boolean | undefined
  error?: string | undefined
  hint?: string | undefined
  htmlFor: string
  className?: string | undefined
  children: ReactNode
}

function FieldShell({
  label,
  required,
  error,
  hint,
  htmlFor,
  className,
  children,
}: FieldShellProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-body-sm text-muted">
        {label}
        {required && <span className="ml-0.5 text-faint">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-caption text-danger">{error}</p>
      ) : (
        hint && <p className="text-caption text-faint">{hint}</p>
      )}
    </div>
  )
}

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string | undefined
  hint?: string | undefined
}

export function TextField({
  label,
  error,
  hint,
  required,
  className,
  id,
  ...rest
}: TextFieldProps) {
  const autoId = useId()
  const inputId = id ?? autoId
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={hint}
      htmlFor={inputId}
      className={className}
    >
      <input id={inputId} required={required} className={cn(controlClass, 'h-11')} {...rest} />
    </FieldShell>
  )
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string
  error?: string | undefined
  hint?: string | undefined
  children: ReactNode
}

export function SelectField({
  label,
  error,
  hint,
  required,
  className,
  id,
  children,
  ...rest
}: SelectFieldProps) {
  const autoId = useId()
  const selectId = id ?? autoId
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={hint}
      htmlFor={selectId}
      className={className}
    >
      <div className="relative">
        <select
          id={selectId}
          required={required}
          className={cn(controlClass, 'h-11 appearance-none pr-10')}
          {...rest}
        >
          {children}
        </select>
        <Icon
          name="chevron-down"
          size={16}
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-muted"
        />
      </div>
    </FieldShell>
  )
}

interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string
  error?: string | undefined
  hint?: string | undefined
}

export function TextareaField({
  label,
  error,
  hint,
  required,
  className,
  id,
  ...rest
}: TextareaFieldProps) {
  const autoId = useId()
  const areaId = id ?? autoId
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={hint}
      htmlFor={areaId}
      className={className}
    >
      <textarea
        id={areaId}
        required={required}
        rows={4}
        className={cn(controlClass, 'py-3')}
        {...rest}
      />
    </FieldShell>
  )
}
